import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { __ } from '@app/shared/functions/object.functions';
import { environment } from '@env/environment';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import deDE from '../../translations/de-DE.json';
import enUS from '../../translations/en-US.json';

const languageKey = 'language';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param s The string to extract for translation.
 * @return The same string.
 */

export function extract(s: string) {
  return s;
}

export class Language {
  locale!: string;
  name!: string;
}

@Service({ autoProvided: false })
export class I18nService {
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  defaultLanguage: Language;

  supportedLanguages!: Language[];

  private _language!: Language;
  private langChangeSubscription?: Subscription;
  constructor() {
    const translateService = this.translateService;

    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en-US', enUS);
    translateService.setTranslation('de-DE', deDE);
    this.defaultLanguage = environment.defaultLanguage;
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */

  init(supportedLanguages: Language[]) {
    this.supportedLanguages = supportedLanguages;
    this.language = undefined;

    this.langChangeSubscription?.unsubscribe();
    this.langChangeSubscription = this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: LangChangeEvent) => {
        localStorage.setItem(languageKey, event.lang);
      });
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */

  set language(language: string | Language | undefined) {
    let langValue =
      (typeof language === 'string' ? language : language?.locale) ||
      localStorage.getItem(languageKey) ||
      this.translateService.getBrowserCultureLang() ||
      this.defaultLanguage.locale;
    let foundLanguage = environment.supportedLanguages.find((q) => q.locale === langValue);

    // If no exact match is found, search without the region
    if (langValue && __.IsNullOrUndefined(foundLanguage)) {
      langValue = langValue.split('-')[0]!;

      foundLanguage = environment.supportedLanguages.find((supportedLanguage) =>
        supportedLanguage.locale.startsWith(langValue),
      );
    }

    // Fallback if language is not supported
    if (__.IsNullOrUndefined(foundLanguage)) {
      foundLanguage = this.defaultLanguage;
    }

    this._language = foundLanguage;

    this.translateService.use(this._language.locale);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */

  get language(): string {
    if (__.IsNullOrUndefined(this._language)) {
      this.language = undefined;
    }
    return this._language.locale;
  }
}
